/* ═══════════════════════════════════════════════════════════════
   RecordLink — a row / card / sidebar item that is a REAL link
   (MULTI_WINDOW_PLAN W2). It must look exactly like the thing it wraps:
   no underline, no link colour. Behaviour:

     plain click   -> onOpen() (or, with `bubble`, the parent row handles it)
     Ctrl/Cmd/Shift click, middle click
                   -> web: the browser's own new tab; desktop: second window
     Enter         -> same as a plain click; Ctrl+Enter -> new tab / window
     right-click   -> desktop only: kit ContextMenu (see useRecordMenu)

   `deferShift`: inside a selectable table, Shift+click is the Explorer
   range gesture — the link leaves it to the row.
   ═══════════════════════════════════════════════════════════════ */

import * as React from 'react';
import { Link } from 'react-router';
import { isWeb } from '../lib/platform';
import { openAddress } from './router';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../components/ui/context-menu';

export interface RecordLinkProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Link>,
  'to' | 'onClick'
> {
  href: string;
  /** What a plain click / Enter does. */
  onOpen?: () => void;
  /** A plain MOUSE click bubbles to a parent handler (DataTable row);
   *  Enter still calls onOpen. */
  bubble?: boolean;
  /** Shift+click belongs to the parent (Explorer range select). */
  deferShift?: boolean;
}

const LINK_RESET: React.CSSProperties = {
  color: 'inherit',
  textDecoration: 'none',
};

export const RecordLink = React.forwardRef<HTMLAnchorElement, RecordLinkProps>(function RecordLink(
  { href, onOpen, bubble, deferShift, style, children, ...rest },
  ref
) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const mod = e.ctrlKey || e.metaKey;
    const keyboard = e.detail === 0;
    if (e.shiftKey && !mod && deferShift && !keyboard) {
      e.preventDefault(); // the row turns it into a range selection
      return;
    }
    if (mod || e.shiftKey) {
      e.stopPropagation(); // never also fire the row's own click
      if (!isWeb) {
        e.preventDefault();
        openAddress(href, { newWindow: true });
      }
      return; // web: the browser opens the new tab itself
    }
    e.preventDefault();
    if (bubble && !keyboard) return;
    if (bubble) e.stopPropagation();
    onOpen?.();
  };

  return (
    <Link
      {...rest}
      ref={ref}
      to={href}
      style={{ ...LINK_RESET, ...style }}
      // A nested control that stops propagation must not let the anchor
      // navigate natively (full page load on web): block it in capture.
      onClickCapture={(e) => {
        const mod = e.ctrlKey || e.metaKey;
        if (!mod && !e.shiftKey) e.preventDefault();
      }}
      onClick={handleClick}
      onAuxClick={(e) => {
        if (e.button === 1 && !isWeb) {
          e.preventDefault();
          e.stopPropagation();
          openAddress(href, { newWindow: true });
        }
      }}
    >
      {children}
    </Link>
  );
});

/* ── Desktop right-click menu ─────────────────────────────────────── */

const ITEM_CLASS = 'cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-hover';

interface MenuState {
  x: number;
  y: number;
  href: string;
  onOpen: () => void;
}

export function RecordContextMenu({
  x,
  y,
  href,
  onOpen,
  onClose,
}: MenuState & { onClose: () => void }) {
  return (
    <ContextMenu open onOpenChange={(next) => !next && onClose()}>
      <ContextMenuTrigger asChild>
        <span style={{ position: 'fixed', left: x, top: y, width: 0, height: 0 }} />
      </ContextMenuTrigger>
      <ContextMenuContent className="min-w-[11.25rem] rounded-md border border-border bg-surface p-1 shadow-lg">
        <ContextMenuItem
          className={ITEM_CLASS}
          onSelect={() => {
            onClose();
            onOpen();
          }}
        >
          Open
        </ContextMenuItem>
        <ContextMenuItem
          className={ITEM_CLASS}
          onSelect={() => {
            onClose();
            openAddress(href, { newWindow: true });
          }}
        >
          Open in new window
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

/** `show(e, href, onOpen)` on a contextmenu event: desktop opens the kit menu
 *  and returns true; web returns false and leaves the browser's own menu
 *  ("Open link in new tab") alone. Render `menu` once, anywhere. */
export function useRecordMenu(): {
  show: (e: React.MouseEvent, href: string | undefined, onOpen: () => void) => boolean;
  menu: React.ReactNode;
} {
  const [state, setState] = React.useState<MenuState | null>(null);
  const show = React.useCallback(
    (e: React.MouseEvent, href: string | undefined, onOpen: () => void) => {
      if (isWeb || !href) return false;
      e.preventDefault();
      setState({ x: e.clientX, y: e.clientY, href, onOpen });
      return true;
    },
    []
  );
  const menu = state ? <RecordContextMenu {...state} onClose={() => setState(null)} /> : null;
  return { show, menu };
}
